import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Icon, STATUS_MAP, StatusChip } from '../components/sp-primitives';
import { CONFIG_BACKEND } from '../config-backend';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';
import { ROUTES } from '../routes';

function firstName(value) {
  const name = String(value || '').trim();
  if (!name || name === '—') return '—';
  return name.split(/\s+/)[0] || '—';
}

function MonthChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="sp-dash-pending-empty" style={{ padding: 24 }}>Sem dados de pagamentos.</div>;
  }
  const totals = data.map((d) => d.total);
  const max = Math.max(...totals, 1);
  const labels = data.map((d) => {
    const m = d.month.split('-')[1];
    return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][Number(m)-1]}`;
  });
  const W = 520; const H = 160; const padT = 8; const padB = 28; const padX = 20;
  const bw = (W - padX * 2) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      {[0.25, 0.5, 0.75].map((p) => {
        const y = padT + (1 - p) * (H - padT - padB);
        return <line key={p} x1={padX} x2={W - padX} y1={y} y2={y} stroke="var(--border)" strokeDasharray="2 4" />;
      })}
      {data.map((d, i) => {
        const h = max > 0 ? (d.total / max) * (H - padT - padB) : 0;
        const isCurrentMonth = d.month === data[data.length - 1].month;
        return (
          <g key={i}>
            <text
              x={padX + i * bw + bw / 2}
              y={H - padB - h - 4}
              textAnchor="middle"
              fontSize={10}
              fontWeight={600}
              fill={isCurrentMonth ? 'var(--ft-green)' : 'var(--fg-muted)'}
            >{d.total}</text>
            <rect
              x={padX + i * bw + 3}
              y={H - padB - h}
              width={bw - 6}
              height={Math.max(h, 1)}
              rx={2}
              fill={isCurrentMonth ? 'var(--ft-green)' : 'var(--ink-700)'}
              opacity={isCurrentMonth ? 1 : 0.35}
            />
            <text
              x={padX + i * bw + bw / 2}
              y={H - 8}
              textAnchor="middle"
              fontSize={9}
              fill="var(--fg-muted)"
            >{labels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
}

function PDashboard() {
  const app = useApp();
  const activeCompanyId = Number(app.activeCompany?.id || 0);
  const [dashData, setDashData] = useState(null);

  useEffect(() => {
    if (!app.authToken || !activeCompanyId) return;
    const url = `${CONFIG_BACKEND.baseUrl}/api/dashboard?company_id=${activeCompanyId}`;
    fetch(url, { headers: { Accept: 'application/json', Authorization: `Bearer ${app.authToken}` } })
      .then((r) => r.json())
      .then(setDashData)
      .catch(() => {});
  }, [app.authToken, activeCompanyId]);

  useLayoutEffect(() => {
    if (!app.workspaceReady) return;
    if (app.companies.length === 0 && app.templates.length === 0) {
      app.navigate(ROUTES.EMPTY);
    }
  }, [app.workspaceReady, app.companies.length, app.templates.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="dashboard" />;
  }
  if (app.companies.length === 0 && app.templates.length === 0) {
    return null;
  }
  const userFirstName = String(app.currentUser?.name || '').trim().split(/\s+/)[0] || 'Usuário';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Bom dia' : (hour < 18 ? 'Boa tarde' : 'Boa noite');
  const companyBatches = app.batches.filter((b) => Number(b.companyId || 0) === activeCompanyId);
  const pending = companyBatches.filter((b) => b.status === 'approval');
  const recentPayments = [...companyBatches]
    .sort((a, b) => Number(b.lotId || 0) - Number(a.lotId || 0))
    .slice(0, 5);
  return (
    <PShell active="dashboard">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Visão geral · {app.activeCompany.name}</div>
          <h1 className="sp-screen-head__title">{greeting}, {userFirstName}</h1>
        </div>
      </div>

      <div className="sp-dash-kpi-grid">
        {(() => {
          const transmittedFromBatches = companyBatches.filter((b) => b.status === 'transmitted' || b.status === 'sent');
          const transmittedQty = dashData?.kpis?.transmitted_count ?? transmittedFromBatches.length;
          const transmittedAmt = dashData?.kpis?.transmitted_amount ?? transmittedFromBatches.reduce((a, b) => a + parseFloat(String(b.total || '0').replace(/[^\d,]/g, '').replace(',', '.')), 0);
          const approvalAmt = pending.reduce((a, b) => a + parseFloat(String(b.total || '0').replace(/[^\d,]/g, '').replace(',', '.')), 0);
          const pendingApprovals = dashData?.kpis?.lots_needing_approval ?? pending.length;
          return [
            { label: 'Qtd. Pagamentos', value: String(transmittedQty), delta: 'transmitidos', kind: 'green' },
            { label: 'Valor Pagamentos', value: `R$ ${Number(transmittedAmt).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, delta: 'transmitidos', kind: 'green' },
            { label: 'Aguardando aprovação', value: `R$ ${approvalAmt.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`, delta: `${pendingApprovals} lotes`, kind: 'amber' },
            { label: 'Aguardando Pendências', value: String(pending.reduce((a, b) => a + (b.diffs || 0), 0)), delta: 'sem dados', kind: 'red' },
          ];
        })().map((kp, i) => (
          <div key={i} className="sp-card sp-dash-kpi-tile">
            <div className="sp-dash-kpi-label">{kp.label}</div>
            <div className="sp-num sp-dash-kpi-value">{kp.value}</div>
            <div className="sp-dash-kpi-chip"><StatusChip kind={kp.kind} dot={false}>{kp.delta}</StatusChip></div>
          </div>
        ))}
      </div>

      <div className="sp-dash-split">
        <div className="sp-card sp-dash-panel">
          <div className="sp-dash-panel__title">Volume de Pagamentos por mês</div>
          <MonthChart data={dashData?.monthly_volume || []} />
        </div>
        <div className="sp-card sp-dash-panel">
          <div className="sp-dash-pending-head">
            <div className="sp-dash-pending-head__title">Aguardando aprovação</div>
            <span className="sp-chip sp-chip-amber">{pending.length} lotes</span>
          </div>
          <div className="sp-dash-pending-list">
            {pending.slice(0, 3).map((b) => (
              <button key={b.id} type="button" className="sp-dash-pending-item" onClick={() => app.navigate('approve')}>
                <div className="sp-dash-pending-item__main">
                  <div className="sp-dash-pending-item__name">{b.template}</div>
                  <div className="sp-mono sp-dash-pending-item__id">{b.id} · {b.ref}</div>
                </div>
                <div className="sp-dash-pending-item__amt">
                  <div className="sp-num sp-dash-pending-item__total">{b.total}</div>
                  {b.diffs > 0 && <div className="sp-dash-pending-item__warn">{b.diffs} divergências</div>}
                </div>
                <Icon name="arrow" size={13} />
              </button>
            ))}
            {pending.length === 0 && <div className="sp-dash-pending-empty">Nenhum lote pendente 🎉</div>}
          </div>
        </div>
      </div>

      <div className="sp-card sp-dash-imports">
        <div className="sp-dash-imports__bar">
          <div className="sp-dash-imports__title">Últimos Pagamentos</div>
          <button type="button" className="sp-btn sp-btn-ghost sp-dash-imports__link" onClick={() => app.navigate('payments')}>Ver pagamentos →</button>
        </div>
        <table className="sp-table">
          <thead><tr><th>Lote</th><th>Criado Em</th><th>Criado Por</th><th>Vencimento</th><th>Doc. Sienge</th><th>Modelo / Arquivo</th><th className="sp-th-num">Valor</th><th className="sp-th-num sp-payments-th-credores">CREDOR(ES)</th><th>Status</th><th /></tr></thead>
          <tbody>
            {recentPayments.map((b) => {
              const s = STATUS_MAP[b.status] || STATUS_MAP.draft;
              const docId = b.siengeDocumentId && String(b.siengeDocumentId).trim();
              const docCell = docId
                ? <span className="sp-chip sp-mono">{docId}</span>
                : b.status === 'pending'
                  ? <span className="sp-chip sp-chip-red">Pendente</span>
                  : b.status === 'approval'
                    ? <span className="sp-chip sp-chip-amber">Aguardando Aprovação</span>
                    : <span className="sp-mono sp-td-date">—</span>;
              return (
                <tr key={b.id}>
                  <td className="sp-mono sp-fw-700">{b.id}</td>
                  <td className="sp-mono sp-td-date">{b.date}</td>
                  <td>{firstName(b.createdByName)}</td>
                  <td className="sp-mono sp-td-date">{b.dueDate ?? '—'}</td>
                  <td>{docCell}</td>
                  <td>
                    <div className="sp-cell-stack-title">{b.template}</div>
                    <div className="sp-mono sp-cell-stack-file">{b.file}</div>
                  </td>
                  <td className="sp-num sp-td-num">{b.total}</td>
                  <td className="sp-num sp-td-muted-num">{b.colab}</td>
                  <td><StatusChip kind={s.kind}>{s.label}</StatusChip></td>
                  <td className="sp-td-actions"><button type="button" className="sp-btn sp-btn-ghost sp-btn-row-action" onClick={() => app.navigate('batch-details', { id: b.id })}>Detalhes <Icon name="chevron" size={11} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PShell>
  );
}

export { PDashboard };
